import React from 'react';

import cx from 'classnames';
import { isEmpty } from 'ramda';
import { Control, Controller } from 'react-hook-form';

import { ILandingSettings } from 'detailsv2/LandingV2/types';
import { isNotDefined } from 'utils/ramda';

import { useItinerariesItemContext } from '../../../utils/itineraries/context';
import cs from '../../Pages/Admin/Itinerary/Details/DetailsChronologicalMode/Event/ModalContents/Edit/index.module.css';
import { AModal } from '../AModal';
import { BaseSelect } from '../BaseFormComponents/Select';
import Button from '../Button';
import Loader from '../Loader';
import Switch from '../Switch';
import { proposalSettingsMap, labelsMap } from './constants';
import useProposalSettings from './functions';
import s from './index.module.css';

export interface IProposalSettings {
  id: string;
  settings: ILandingSettings;
  onClose: () => void;
  withMultipleUsersWarning: (...args: any[]) => (...args: any[]) => void;
}

const ProposalSettings = (props: IProposalSettings) => {
  const { onClose, languageChangeHandler, selectedLanguage, submitHandler, locales, control, isDirty, isSubmitted } =
    useProposalSettings(props);

  return (
    <AModal
      size="auto"
      header="Itinerary Settings"
      isOpen
      onClose={onClose}
      bodyClassName={s.modalBody}
      headerClassName={s.modalHeader}
    >
      {isSubmitted && <Loader isFillBlock />}
      <div data-testid="settings-modal" className={s.wrapper}>
        <ProposalSettingsGroup group="display" settings={props.settings} control={control} />
        <ProposalSettingsGroup group="date" settings={props.settings} control={control} />
        <div className={s.languageSettings}>
          <div className={s.heading}>Itinerary Language</div>
          <div className={s.languageSelect}>
            This will change the language for default itinerary text only (eg, dates). Note that anything you pull in
            from the Content Library will not automatically be translated.
            <Controller
              control={control}
              name="language"
              render={({ field: { onChange } }) => (
                <BaseSelect
                  value={selectedLanguage || ''}
                  options={locales}
                  onChange={(id, name) => {
                    languageChangeHandler(name);
                    onChange(id);
                  }}
                  placeholder="Select Language"
                />
              )}
            />
          </div>
        </div>
      </div>
      <div className={cx(cs.footer, s.modalFooter)} data-testid="settings-modal-footer">
        <Button
          className={s.defaultBtn}
          type="default"
          size="medium"
          onClick={props.withMultipleUsersWarning(submitHandler(true))}
          disabled={!isDirty || isSubmitted}
        >
          Make Default & Save
        </Button>
        <Button
          className={s.saveBtn}
          size="medium"
          onClick={props.withMultipleUsersWarning(submitHandler(false))}
          disabled={!isDirty || isSubmitted}
        >
          Save
        </Button>
      </div>
    </AModal>
  );
};

export function ProposalSettingsGroup({
  group,
  settings,
  control,
}: {
  group: keyof typeof proposalSettingsMap;
  settings: ILandingSettings;
  control: Control;
}) {
  if (isNotDefined(settings)) {
    return null;
  }

  const groupSettings = proposalSettingsMap[group]
    .filter((name) => name in settings)
    .map((name) => (
      <Controller
        key={name}
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Switch active={value} onClick={() => onChange(!value)} label={labelsMap[name]} />
        )}
      />
    ));

  if (isEmpty(groupSettings)) {
    return null;
  }

  return (
    <>
      <div className={s[`${group}Settings`]}>{groupSettings}</div>
      <div className={s.divider} />
    </>
  );
}

export default ProposalSettings;
